import React, { useState } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { status, data } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) {
      const e = data.detail[0];
      return typeof e === 'object' ? `${e.loc?.[e.loc.length - 1] || 'Field'}: ${e.msg || 'Invalid'}` : String(e);
    }
    if (typeof data.detail === 'string') return data.detail;
  }
  return data.detail || data.message || `Request failed (${status})`;
};

const SupplierForm = ({ supplier, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: supplier?.name || '',
    supplier_type: supplier?.supplier_type || 'business',
    email: supplier?.email || '',
    phone: supplier?.phone || '',
    ntn: supplier?.ntn || '',
    address: supplier?.address || '',
    city: supplier?.city || '',
    credit_limit: supplier?.credit_limit ? String(supplier.credit_limit) : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) { setError('Name is required'); return; }
    setLoading(true);
    const submitData = {
      ...formData,
      credit_limit: formData.credit_limit === '' ? 0 : Number(formData.credit_limit),
    };
    try {
      if (supplier) {
        await api.put(`/suppliers/${supplier.id}`, submitData);
      } else {
        await api.post('/suppliers', submitData);
      }
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => setFormData({ ...formData, [key]: val });

  return (
    <ModalShell
      title={supplier ? 'Edit Supplier' : 'Add Supplier'}
      onClose={onClose}
      maxWidth="max-w-2xl"
      dataTestId="supplier-form-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors"
            data-testid="cancel-supplier-form"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="supplier-form"
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="save-supplier-button"
          >
            {loading ? 'Saving...' : (supplier ? 'Update Supplier' : 'Add Supplier')}
          </button>
        </div>
      }
    >
      <form id="supplier-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="supplier-form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Supplier Name {requiredMark}</label>
            <input type="text" className={inputClass} value={formData.name} onChange={(e) => set('name', e.target.value)} required data-testid="supplier-name-input" />
          </div>
          <div>
            <label className={labelClass}>Type</label>
            <select className={selectClass} value={formData.supplier_type} onChange={(e) => set('supplier_type', e.target.value)} data-testid="supplier-type-select">
              <option value="business">Business</option>
              <option value="individual">Individual</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>NTN</label>
            <input type="text" className={inputClass} value={formData.ntn} onChange={(e) => set('ntn', e.target.value)} placeholder="National Tax Number" data-testid="supplier-ntn-input" />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input type="text" className={inputClass} value={formData.phone} onChange={(e) => set('phone', e.target.value)} data-testid="supplier-phone-input" />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" className={inputClass} value={formData.email} onChange={(e) => set('email', e.target.value)} data-testid="supplier-email-input" />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input type="text" className={inputClass} value={formData.city} onChange={(e) => set('city', e.target.value)} data-testid="supplier-city-input" />
          </div>
          <div>
            <label className={labelClass}>Credit Limit (PKR)</label>
            <input type="number" min="0" step="0.01" className={inputClass} value={formData.credit_limit} onChange={(e) => set('credit_limit', e.target.value)} placeholder="0" data-testid="supplier-credit-limit-input" />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Address</label>
            <textarea className={textareaClass} rows="2" value={formData.address} onChange={(e) => set('address', e.target.value)} data-testid="supplier-address-input" />
          </div>
        </div>
      </form>
    </ModalShell>
  );
};

export default SupplierForm;
